/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import * as Dialog from '@radix-ui/react-dialog'
import { FiX } from 'react-icons/fi'
import { useOptimizedCartStore } from '@/stores/optimized-cart-store'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { QuantityControl, DeleteButton } from '@/app/[locale]/cart/CartClient'
import OptimizedImage from '@/components/OptimizedImage'
import { useLocale, useTranslations } from 'next-intl'
import { convertPriceWithRate, formatCurrency } from '@/i18n/utils'
import { useRouter } from 'next/navigation'
import { useState, useEffect, useCallback, useMemo } from 'react'

export default function CartSidebar({ isB2B = false }: { isB2B?: boolean }) {
  const locale = useLocale()
  const t = useTranslations('checkout')
  const cartT = useTranslations('cart')
  const router = useRouter()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  // Express Checkout error state (displayed above the wallet buttons)
  const [fallbackProducts, setFallbackProducts] = useState<any[]>([])
  const [isFetchingProducts, setIsFetchingProducts] = useState(false)
  const [expressCheckoutShippingCost] = useState<number | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [showError, setShowError] = useState(false)

  // Phone collection dialog state from store
  const showPhoneDialog = useOptimizedCartStore(state => state.showPhoneDialog)

  const userCurrency = 'EUR'
  const isSidebarOpen = useOptimizedCartStore(state => state.isSidebarOpen)
  const closeSidebar = useOptimizedCartStore(state => state.closeSidebar)
  const items = useOptimizedCartStore(state => state.items)
  const initialProducts = useOptimizedCartStore(state => state.initialProducts)
  const syncCart = useOptimizedCartStore(state => state.syncCart)
  const forceSync = useOptimizedCartStore(state => state.forceSync)
  // Get error state directly from store

  // Handle close with phone dialog check
  const handleClose = useCallback(() => {
    // Don't close if phone dialog is open or checkout is processing
    if (showPhoneDialog) {
      console.log('🚫 Preventing cart sidebar close - phone dialog is open')
      return
    }
    if (isProcessing) {
      console.log('🚫 Preventing cart sidebar close - checkout processing')
      return
    }
    setIsVisible(false)
    setTimeout(() => {
      closeSidebar()
    }, 300) // Match the animation duration
  }, [showPhoneDialog, isProcessing, closeSidebar])


  // Use initialProducts if available, otherwise fallback products
  const availableProducts = initialProducts && initialProducts.length > 0 ? initialProducts : fallbackProducts;

  // Memoize enriched cart items to prevent infinite re-renders
  const enrichedCartItems = useMemo(() => {
    return items.map((item: any) => {
      const product = availableProducts?.find((p: any) => p.id === item.productId);
      if (!product) {
        console.warn('⚠️ No product found for cart item:', item.productId, 'Available products:', availableProducts?.map(p => p.id));
        console.warn('⚠️ Cart item details:', item);
        console.warn('⚠️ Available products count:', availableProducts?.length);
        return null;
      }

      return {
        id: item.id,
        quantity: item.quantity,
        selectedColor: item.selectedColor,
        skuId: item.skuId,
        product
      };
    }).filter(Boolean);
  }, [items, availableProducts]);

  // Handle visibility state for smooth animations
  useEffect(() => {
    if (isSidebarOpen) {
      setIsVisible(true)
      // Sync cart when opening sidebar
      syncCart()

      // Force a re-render to ensure items are displayed
      const timer = setTimeout(() => {
        // This will trigger a re-render if items were added but not showing
        if (items.length > 0 && enrichedCartItems.length === 0) {
          console.log('🔄 Forcing cart sync due to missing enriched items')
          syncCart()
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [isSidebarOpen, syncCart, items.length])

  // Listen for cart changes and ensure sidebar updates
  useEffect(() => {
    if (isSidebarOpen) {
      // Small delay to allow for state updates
      const timer = setTimeout(() => {
        // If we have items but no enriched items, something is wrong
        if (items.length > 0 && enrichedCartItems.length === 0) {
          console.log('🔄 Cart items changed but no enriched items - forcing refresh');
          forceSync();
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [items, isSidebarOpen, forceSync])

  // Close sidebar when navigating to any page
  useEffect(() => {
    const handleRouteChange = () => {
      if (isSidebarOpen) {
        closeSidebar()
      }
    }

    // Listen for route changes
    const originalPush = router.push
    const originalReplace = router.replace

    router.push = (...args) => {
      handleRouteChange()
      return originalPush.apply(router, args)
    }

    router.replace = (...args) => {
      handleRouteChange()
      return originalReplace.apply(router, args)
    }

    // Cleanup
    return () => {
      router.push = originalPush
      router.replace = originalReplace
    }
  }, [isSidebarOpen, closeSidebar, router])

  // Handle ESC key with phone dialog check
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      // Don't close cart sidebar if phone dialog is open
      if (showPhoneDialog) {
        console.log('🚫 Preventing ESC close - phone dialog is open')
        return
      }
      // Block ESC closing while Express Checkout is processing
      if (isProcessing) {
        console.log('🚫 Preventing ESC close - checkout processing')
        return
      }
      if (event.key === 'Escape' && isSidebarOpen) {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [isSidebarOpen, showPhoneDialog, isProcessing, handleClose])

  // Handle backdrop click with phone dialog check  
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Don't close if phone dialog is open or checkout is processing
    if (showPhoneDialog) {
      console.log('🚫 Preventing backdrop close - phone dialog is open')
      return
    }
    if (isProcessing) {
      console.log('🚫 Preventing backdrop close - checkout processing')
      return
    }
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  // Enhanced fallback mechanism: fetch products if we have cart items but no matching products
  useEffect(() => {
    const needsFallback = items.length > 0 && (
      (!initialProducts || initialProducts.length === 0) ||
      items.some(item => !initialProducts?.find(p => p.id === item.productId))
    );

    if (needsFallback && !isFetchingProducts) {
      console.log('🔄 Fetching fallback products for cart items');
      setIsFetchingProducts(true);

      // Get unique product IDs from cart items
      const productIds = [...new Set(items.map(item => item.productId))];

      // Fetch products from the API
      fetch('/api/products?' + new URLSearchParams({
        ids: productIds.join(',')
      }))
        .then(response => response.json())
        .then(data => {
          console.log('✅ Fetched fallback products:', data.products?.length);
          setFallbackProducts(data.products || []);
          setIsFetchingProducts(false);

          // Also update the cart store with these products
          if (data.products && data.products.length > 0) {
            data.products.forEach((product: any) => {
              useOptimizedCartStore.getState().addProductToCache(product);
            });
          }
        })
        .catch((error) => {
          console.error('❌ Failed to fetch fallback products:', error);
          setIsFetchingProducts(false);
          setShowError(true);

          // Auto-retry up to 3 times with exponential backoff
          if (retryCount < 3) {
            const retryDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              setShowError(false);
            }, retryDelay);
          }
        });
    }
  }, [items, initialProducts, isFetchingProducts]);

  // Debug logging for cart synchronization issues
  useEffect(() => {
    if (isSidebarOpen) {
      console.log('🛒 Cart Sidebar Debug Info:');
      console.log('- Items in store:', items.length, items);
      console.log('- Initial products:', initialProducts?.length, initialProducts?.map(p => ({ id: p.id, name: p.name })));
      console.log('- Fallback products:', fallbackProducts.length, fallbackProducts?.map(p => ({ id: p.id, name: p.name })));
      console.log('- Available products:', availableProducts?.length, availableProducts?.map(p => ({ id: p.id, name: p.name })));
      console.log('- Enriched cart items:', enrichedCartItems.length, enrichedCartItems);

      if (items.length > 0 && enrichedCartItems.length === 0) {
        console.error('🚨 CART SYNC ISSUE: Items exist but no enriched items found!');
        console.log('🔄 Attempting to force sync and refresh...');
        forceSync();

        // Try to fetch missing products immediately
        const missingProductIds = items
          .filter(item => !availableProducts?.find(p => p.id === item.productId))
          .map(item => item.productId);

        if (missingProductIds.length > 0) {
          console.log('🔍 Fetching missing products:', missingProductIds);
          fetch('/api/products?' + new URLSearchParams({
            ids: missingProductIds.join(',')
          }))
            .then(response => response.json())
            .then(data => {
              if (data.products) {
                setFallbackProducts(prev => [...prev, ...data.products]);
                data.products.forEach((product: any) => {
                  useOptimizedCartStore.getState().addProductToCache(product);
                });
              }
            })
            .catch(console.error);
        }
      }
    }
  }, [isSidebarOpen, items, initialProducts, fallbackProducts, availableProducts, forceSync, enrichedCartItems]);

  // Helper function to get the image URL for a cart item
  const getItemImageUrl = (item: any): string | null => {
    try {
      if (!item || !item.product || !item.selectedColor) {
        return item?.product?.product_image || null;
      }

      const options = item.product.options;
      if (!options) {
        return item.product.product_image || null;
      }

      let parsedOptions = options;
      if (typeof options === 'string') {
        try {
          parsedOptions = JSON.parse(options);
        } catch {
          return item.product.product_image || null;
        }
      }

      if (Array.isArray(parsedOptions)) {
        const selectedOption = parsedOptions.find((opt: any) => opt.name === item.selectedColor);
        if (selectedOption && selectedOption.image) {
          return selectedOption.image;
        }
      }

      return item.product.product_image || null;
    } catch {
      return item?.product?.product_image || null;
    }
  };

  // Calculate price per kg (matching checkout logic exactly)
  function calculatePricePerKg(quantityGrams: number, priceChart: Record<string, number> | undefined, basePrice: number): number {
    try {
      if (typeof basePrice !== 'number' || isNaN(basePrice)) {
        return 0;
      }

      if (!priceChart) return basePrice;

      if (typeof quantityGrams !== 'number' || isNaN(quantityGrams) || quantityGrams <= 0) {
        return basePrice;
      }

      const quantityKg = quantityGrams / 1000;

      if (typeof priceChart !== 'object' || priceChart === null || Object.keys(priceChart).length === 0) {
        return basePrice;
      }

      const ranges = Object.entries(priceChart)
        .filter(([range]) => typeof range === 'string' && range.trim().length > 0)
        .sort((a, b) => {
          try {
            const aLower = parseFloat(a[0].split('-')[0]);
            const bLower = parseFloat(b[0].split('-')[0]);
            return isNaN(aLower) || isNaN(bLower) ? 0 : aLower - bLower;
          } catch {
            return 0;
          }
        });

      if (ranges.length === 0) return basePrice;

      for (const [range, price] of ranges) {
        try {
          if (!range) continue;

          const bounds = range.replace('kg', '').split('-').map(n => parseFloat(n.trim()));

          if (!bounds || bounds.length === 0) continue;

          const lower = bounds[0];
          const upper = bounds.length > 1 ? bounds[1] : null;

          if (isNaN(lower)) continue;

          if (quantityGrams <= 100 && lower === 0) {
            return typeof price === 'number' ? price : basePrice;
          }

          if (quantityKg >= lower && (!upper || isNaN(upper) || quantityKg <= upper)) {
            return typeof price === 'number' ? price : basePrice;
          }
        } catch (error) {
          console.error('Error parsing price range:', error, range);
          continue;
        }
      }

      return basePrice;
    } catch (error) {
      console.error('Error calculating price per kg:', error);
      return basePrice;
    }
  }

  // Calculate API item price (matching checkout logic exactly)
  function calculateApiItemPrice(item: any): number {
    try {
      if (!item || !item.product || typeof item.product.price_per_kg !== 'number') {
        return 0;
      }

      const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
      if (quantity <= 0) {
        return 0;
      }

      const isKgPrice = item.product.kg_price !== false;
      const quantityKg = isKgPrice ? quantity / 1000 : quantity;

      const basePricePerKg = calculatePricePerKg(
        quantity,
        item.product?.price_chart,
        item.product.price_per_kg
      );

      let extraPrice = 0;

      if (item.selectedColor && item.product.options) {
        let options = item.product.options;

        if (typeof options === 'string') {
          try {
            options = JSON.parse(options);
          } catch (error) {
            console.error('Error parsing product options:', error);
            options = [];
          }
        }

        if (Array.isArray(options)) {
          const selectedOption = options.find((opt: any) => opt && opt.name === item.selectedColor);
          if (selectedOption && typeof selectedOption.extraPrice === 'number') {
            extraPrice = selectedOption.extraPrice;
          }
        }
      }

      const finalPricePerKg = basePricePerKg + extraPrice;

      return isKgPrice ? quantityKg * finalPricePerKg : finalPricePerKg * quantity;
    } catch (error) {
      console.error('Error calculating item price:', error);
      return 0;
    }
  }

  // Calculate totals (matching checkout logic)
  const baseTotal = enrichedCartItems.reduce((sum, item) => {
    if (!item || !item.product) return sum;
    return sum + calculateApiItemPrice(item);
  }, 0);

  // For B2B, do not include VAT in the total; for B2C, VAT is included in the price
  const vatAmount = isB2B ? 0 : baseTotal * 0.2; // 20% VAT for B2C, 0 for B2B
  const finalTotal = isB2B ? baseTotal : baseTotal; // For B2B, total is net (excluding VAT)

  const totalItems = enrichedCartItems.reduce((sum, item) => sum + (item?.quantity || 0), 0);

  // Custom checkout button that closes sidebar
  const handleCheckout = async () => {
    if (isProcessing) return

    try {
      setIsProcessing(true)

      // Close the sidebar first
      handleClose()

      // Small delay to let the sidebar close animation start
      setTimeout(() => {
        router.push(`/${locale}/checkout`)
        setIsProcessing(false)
      }, 100)
    } catch (error) {
      console.error('Error during checkout:', error)
      setIsProcessing(false)
    }
  }

  return (
    <>
      {/* Custom CSS for smooth animations */}
      {/* Using Tailwind classes instead of custom styles for animations and glass effects */}

      <Dialog.Root open={isSidebarOpen} onOpenChange={(open) => !open && handleClose()}>
        <Dialog.Portal>
          {/* No SVG filters needed - using Tailwind's built-in backdrop-blur instead */}

          <Dialog.Overlay
            className={`fixed inset-0 bg-static-black/70 z-[120] transition-all ${isVisible ? 'opacity-100' : 'opacity-0 backdrop-blur-none'
              } ${showPhoneDialog ? 'pointer-events-none' : ''}`}
            onClick={handleBackdropClick}
            style={{
              transitionProperty: 'opacity, backdrop-filter',
              transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          />

          <Dialog.Content className={`fixed top-0 right-0 bottom-0 h-full w-full md:max-w-md focus:outline-none z-[125] backdrop-blur-xl  transition-all duration-500 ease-in-out ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
            } ${showPhoneDialog ? 'pointer-events-none' : ''}`}
            style={{
              transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
            }}>
            {/* Simplified glass container with Tailwind */}
            <div className="relative h-full overflow-hidden transform-gpu">
              {/* Single glass background with gradients */}


              {/* Simple border */}
              <div className="absolute inset-0 border-l border-white/10 shadow-inner" />

              {/* Content area with glass containers */}
              <div className="relative h-full flex flex-col filter drop-shadow-2xl">
                {/* Glass header with Tailwind */}
                <div className={`relative overflow-hidden bg-static-white/5 border-b border-white/10 p-4 md:p-6 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                  }`} style={{ transitionDelay: '100ms' }}>
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Dialog.Title className="text-lg md:text-xl font-semibold text-white drop-shadow-lg">
                        {t('summary.title')} ({totalItems} {totalItems === 1 ? t('summary.unit') : t('summary.units')})
                      </Dialog.Title>
                    </div>
                    <Dialog.Close
                      className="text-white/80 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10 touch-manipulation focus:outline-none focus:ring-0"
                      onClick={handleClose}
                    >
                      <FiX size={20} />
                      <VisuallyHidden>Close</VisuallyHidden>
                    </Dialog.Close>
                  </div>


                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto">
                  {/* Error display */}
                  {showError && (
                    <div className="p-4 mx-4 mt-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-red-200 text-sm">Failed to load cart items</span>
                        </div>
                        <button
                          onClick={() => {
                            setShowError(false);
                            setRetryCount(0);
                            syncCart();
                          }}
                          className="text-red-300 hover:text-red-100 text-sm underline"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  )}

                  {enrichedCartItems.length === 0 && !showError ? (
                    <div className={`p-6 text-center ${isVisible ? 'cart-header-enter' : ''}`}>
                      {isFetchingProducts ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <p className="text-white/70">Loading cart items...</p>
                        </div>
                      ) : (
                        <p className="text-white/70 mb-4">{t('summary.empty')}</p>
                      )}
                    </div>
                  ) : enrichedCartItems.length > 0 ? (
                    <div className="p-3 md:p-4 space-y-3">
                      {enrichedCartItems.map((item, index) => {
                        if (!item || !item.product) return null;

                        const itemPrice = calculateApiItemPrice(item);
                        const { convertedAmount } = convertPriceWithRate(itemPrice, userCurrency);

                        return (
                          <div
                            key={item.id}
                            className={`relative overflow-hidden rounded-xl bg-white/3 backdrop-blur-md border border-white/10 p-3 md:p-4 touch-manipulation transition-all duration-400 ${isVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-5 scale-95'
                              }`}
                            style={{
                              transitionDelay: isVisible ? `${index * 50}ms` : '0ms',
                              transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-transparent to-white/5 rounded-xl" />
                            <div className="relative flex items-start space-x-3">
                              {/* Product image */}
                              <div className="relative overflow-hidden rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 p-[2px] w-12 h-12 md:w-16 md:h-16 flex-shrink-0">
                                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-lg" />
                                {getItemImageUrl(item) ? (
                                  <OptimizedImage
                                    src={getItemImageUrl(item)!}
                                    alt={item.product ? (item.product as any).name || 'Product' : 'Product'}
                                    width={48}
                                    height={48}
                                    className="object-cover rounded-md relative z-10 w-full h-full"
                                    priority={true}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-white/50 relative z-10">
                                    <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                    </svg>
                                  </div>
                                )}
                              </div>

                              {/* Product details */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-white font-medium text-xs md:text-sm truncate mb-1">
                                  {(item.product as any)?.name || 'Product'}
                                </h3>

                                {/* Quantity control in glass container */}
                                <div className="relative mt-2">
                                  <div className="relative overflow-hidden rounded-xl bg-white/3 backdrop-blur-sm border border-white/10 p-2">
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-transparent to-white/5 rounded-xl" />
                                    <div className="relative">
                                      <QuantityControl
                                        itemId={item.id}
                                        productId={item.product.id}
                                        currentQuantity={item.quantity}
                                        product={item.product}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Price and actions */}
                              <div className="flex flex-col items-end space-y-2">
                                <span className="text-white font-semibold text-xs md:text-sm">
                                  {formatCurrency(convertedAmount, locale, userCurrency)}
                                </span>

                                {/* Delete button in glass container */}
                                <div className="relative overflow-hidden rounded-lg bg-white/3 backdrop-blur-sm border border-white/10">
                                  <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-transparent to-white/5 rounded-lg" />
                                  <div className="relative">
                                    <DeleteButton itemId={item.id} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                {/* Glass footer with totals and checkout */}
                {enrichedCartItems.length > 0 && (
                  <div className={`relative overflow-hidden backdrop-blur-md bg-white/5 border-t border-white/10 p-4 md:p-6 transition-all duration-600 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                    }`} style={{ transitionDelay: '200ms' }}>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-transparent to-white/5" />
                    <div className="relative space-y-3">
                      {/* Subtotal */}
                      <div className="flex justify-between text-white/80 text-sm">
                        <span>{t('summary.subtotal')}</span>
                        <span>{formatCurrency(convertPriceWithRate(isB2B ? baseTotal : baseTotal / 1.25, userCurrency).convertedAmount, locale, userCurrency)}</span>
                      </div>

                      {/* Shipping (net cost) */}
                      {expressCheckoutShippingCost !== null && (
                        <div className="flex justify-between text-white/80 text-sm">
                          <span>{t('summary.shipping')}</span>
                          <span>{formatCurrency(expressCheckoutShippingCost, locale, userCurrency)}</span>
                        </div>
                      )}

                      {/* VAT (including shipping VAT) - only show for B2C */}
                      {!isB2B && (
                        <div className="flex justify-between text-white/80 text-sm">
                          <span>{t('summary.vatIncluded')} (20%)</span>
                          <span>
                            {formatCurrency(
                              convertPriceWithRate(vatAmount, userCurrency).convertedAmount +
                              (expressCheckoutShippingCost !== null ? expressCheckoutShippingCost * 0.2 : 0),
                              locale,
                              userCurrency
                            )}
                          </span>
                        </div>
                      )}
                      {/* Total (cart subtotal + net shipping + shipping VAT) */}
                      <div className="flex justify-between text-white font-semibold text-base md:text-lg">
                        <span>{t('summary.total')}</span>
                        <span>
                          {formatCurrency(
                            convertPriceWithRate(finalTotal, userCurrency).convertedAmount +
                            (expressCheckoutShippingCost !== null ? expressCheckoutShippingCost * (isB2B ? 1 : 1.2) : 0),
                            locale,
                            userCurrency
                          )}
                        </span>
                      </div>

                      {/* Express Checkout Section */}
                      <div className="mt-4">
                        <div className="relative overflow-hidden">
                          <div className="relative space-y-3">
                            {/*<ExpressCheckout
                              amount={expressCheckoutAmount}
                              currency={userCurrency.toLowerCase()}
                              isSubmitting={isProcessing}
                              onPaymentError={handleExpressPaymentError}
                              onShippingUpdate={handleExpressCheckoutShippingUpdate}
                              onRedirect={() => {
                                setIsProcessing(false)
                                closeSidebar()
                              }}
                              onPhoneCollectionNeeded={handlePhoneCollectionNeeded}
                              onExpressCheckoutInitiated={handleExpressCheckoutInitiated}
                              onExpressCheckoutReset={handleExpressCheckoutReset}
                              onProcessingChange={handleExpressCheckoutProcessingChange}
                              customerProfile={customerProfile}
                            />*/}
                          </div>
                        </div>
                      </div>



                      {/* Custom checkout button that closes sidebar */}
                      <div className="mt-4 transition-all duration-500 ease-in-out opacity-100 transform translate-y-0 max-h-20">
                        <button
                          onClick={handleCheckout}
                          className={`w-full bg-gradient-to-r from-primary to-primary-light text-bg-main font-medium rounded-full px-4 md:px-6 py-3 md:py-4 text-sm md:text-base
                            transform transition-all duration-200 touch-manipulation
                            ${isProcessing ?
                              'opacity-70 cursor-not-allowed' :
                              'hover:from-primary/90 hover:to-primary-light/90 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 active:translate-y-0 active:scale-95'
                            }`}
                          disabled={isProcessing}
                        >
                          <span className="flex items-center justify-center">
                            {isProcessing ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 md:h-5 md:w-5 text-bg-main" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {cartT('checkout.processing')}
                              </>
                            ) : (
                              cartT('checkout.button')
                            )}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
} 